import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import Layout from '@/components/Layout';
import { GlassCard } from '@/components/ui/glass-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  MessageCircle, Mic, Users, Trophy, BookOpen, Target,
  CheckCircle, ArrowRight, Star, Sparkles, Award,
  ChevronDown, Volume2, Globe, PenTool, Heart,
  Phone, Quote, Languages, Presentation
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Animated Counter Component
function AnimatedCounter({ value, suffix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = parseInt(value);
      const increment = end / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [isInView, value, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

// Wave Separator Component
function WaveSeparator({ color = '#f8fafc', flip = false }) {
  return (
    <div className={`w-full overflow-hidden ${flip ? 'rotate-180' : ''}`} style={{ marginTop: '-1px', marginBottom: '-1px' }}>
      <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-16">
        <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill={color}></path>
      </svg>
    </div>
  );
}

export default function EnglishSpeakProgram() {
  const [openFaq, setOpenFaq] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    studentName: '', grade: '', parentName: '', contact: '', email: '', motivation: ''
  });

  const features = [
    { icon: BookOpen, title: 'Phonics & Grammar', desc: 'Strong foundation in English language rules and pronunciation' },
    { icon: Languages, title: 'Vocabulary Building', desc: 'Learn 2000+ words through interactive and fun methods' },
    { icon: Mic, title: 'Speaking Exercises', desc: 'Daily practice sessions for fluency and confidence' },
    { icon: Users, title: 'Group Discussions', desc: 'Collaborative learning through peer interaction' },
    { icon: MessageCircle, title: 'Storytelling', desc: 'Express ideas through creative narratives and stories' },
    { icon: Volume2, title: 'Pronunciation', desc: 'Perfect accent and clear articulation training' },
    { icon: Presentation, title: 'Debate Training', desc: 'Build confidence in public speaking and arguments' },
  ];

  const benefits = [
    { text: 'Enhanced communication in English', icon: MessageCircle },
    { text: 'Better academic performance across subjects', icon: Award },
    { text: 'Improved self-expression and confidence', icon: Heart },
    { text: 'Personality development and soft skills', icon: Sparkles },
    { text: 'Success in public forums and competitions', icon: Trophy }
  ];

  const stats = [
    { value: 2000, suffix: '+', label: 'Words Learned' },
    { value: 50, suffix: '+', label: 'Club Activities' },
    { value: 15, suffix: '+', label: 'Events Hosted' },
    { value: 100, suffix: '+', label: 'Certificates Issued' }
  ];

  const testimonials = [
    {
      quote: "My daughter now speaks confidently in front of the whole class. Her transformation is amazing!",
      name: "Mrs. Patel",
      role: "Parent of Class 5 Student",
      avatar: "👩"
    },
    {
      quote: "I won first prize in the district debate competition. This program changed my life!",
      name: "Priya S.",
      role: "Class 10 Student",
      avatar: "👧"
    },
    {
      quote: "Students show remarkable improvement in communication. Their confidence has soared.",
      name: "Ms. Kapoor",
      role: "English Teacher",
      avatar: "👩‍🏫"
    }
  ];

  const faqs = [
    { q: 'Which grades can join this program?', a: 'The program is available for students from Class 1 to Class 12, with age-appropriate content and activities for each level.' },
    { q: 'What is the weekly schedule?', a: 'Classes are conducted 3 times per week as part of the regular curriculum, plus additional club activities and speaking sessions.' },
    { q: 'Do students need prior English knowledge?', a: 'No prior fluency needed! We start from the basics and gradually build proficiency. Students of all levels are welcome.' },
    { q: 'Will certificates be provided?', a: 'Yes! Students receive certificates after completing each level and participating in debates, presentations, and events.' },
    { q: 'How do you track progress?', a: 'We use regular speaking assessments, vocabulary tests, presentations, and portfolio tracking to monitor improvement.' },
    { q: 'Are there competitions and events?', a: 'Yes! Regular debates, storytelling competitions, elocutions, and annual English Day celebrations.' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/programs/enroll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          programType: 'english-speak',
          ...formData
        })
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.detail || 'Submission failed');

      toast.success(`Enrollment submitted! ID: ${data.enrollmentId}`);
      setFormData({ studentName: '', grade: '', parentName: '', contact: '', email: '', motivation: '' });
    } catch (error) {
      toast.error(error.message || 'Failed to submit enrollment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section
        className="relative py-24 md:py-32 bg-gradient-to-br from-emerald-600 via-teal-700 to-cyan-800 text-white overflow-hidden"
        data-testid="hero-section"
      >
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 right-10 w-64 h-64 bg-yellow-400/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-green-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-teal-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center px-4 py-2 bg-yellow-400/20 border border-yellow-400/30 rounded-full text-yellow-300 text-sm font-semibold mb-6">
                <Mic className="w-4 h-4 mr-2" />
                Communication Excellence
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                English & Communication
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-amber-400">
                  Skills Program
                </span>
              </h1>
              <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
                Express, Communicate & Succeed. Building confident speakers and effective communicators for a globalized world.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#enroll">
                  <Button size="lg" className="w-full sm:w-auto bg-yellow-400 hover:bg-yellow-500 text-emerald-900 font-bold text-lg px-8 py-6 rounded-xl shadow-lg shadow-yellow-400/30">
                    Start Learning Today
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <a href="tel:9755402604">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white/30 text-white hover:bg-white/10 font-semibold text-lg px-8 py-6 rounded-xl">
                    <Phone className="mr-2 w-5 h-5" />
                    Call Us
                  </Button>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-teal-600 rounded-3xl blur-2xl opacity-30"></div>
                <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20">
                  <div className="grid grid-cols-3 gap-4">
                    {[Mic, MessageCircle, BookOpen, Globe, Trophy, Star].map((Icon, i) => (
                      <motion.div
                        key={i}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.5 + i * 0.1 }}
                        className="aspect-square bg-white/10 rounded-2xl flex items-center justify-center"
                      >
                        <Icon className="w-8 h-8 text-yellow-300" />
                      </motion.div>
                    ))}
                  </div>
                  <div className="mt-6 text-center">
                    <p className="text-emerald-100 text-sm">Building Confident Communicators</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <WaveSeparator color="#ffffff" />

      {/* Why This Program */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-blue-900 mb-4 md:mb-6">
              Why This Program Matters
            </h2>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto">
              English is the global language of opportunity. Mastering it opens doors to better education, careers, and personal growth.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {[
              { icon: Globe, text: 'Global language importance', color: 'from-emerald-500 to-teal-600' },
              { icon: Sparkles, text: 'Confidence in public speaking', color: 'from-amber-500 to-orange-600' },
              { icon: Award, text: 'Better school performance', color: 'from-blue-500 to-indigo-600' },
              { icon: Target, text: 'International opportunities', color: 'from-purple-500 to-pink-600' }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard className="text-center p-6 md:p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 h-full">
                  <div className={`w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                    <item.icon className="w-7 h-7 md:w-8 md:h-8 text-white" />
                  </div>
                  <p className="font-semibold text-slate-700 text-sm md:text-base">{item.text}</p>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <WaveSeparator color="#f0fdf4" />

      {/* Features Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-green-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-blue-900 mb-4">
              Program Features
            </h2>
            <p className="text-lg md:text-xl text-slate-600">Comprehensive training for complete language mastery</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                viewport={{ once: true }}
              >
                <GlassCard className="h-full hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 group">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <feature.icon className="w-7 h-7 md:w-8 md:h-8 text-white" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-blue-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm md:text-base">{feature.desc}</p>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <WaveSeparator color="#ffffff" />

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-blue-900 mb-4">
              Key Benefits
            </h2>
            <p className="text-lg text-slate-600">Transform your communication skills</p>
          </motion.div>

          <div className="space-y-3 md:space-y-4">
            {benefits.map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center space-x-4 p-4 md:p-5 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-100 hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <p className="text-base md:text-lg font-semibold text-slate-800">{benefit.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Teach */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-teal-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-blue-900 mb-4">
              How We Teach
            </h2>
            <p className="text-lg text-slate-600">A proven approach to language mastery</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              { num: '1', title: 'Interactive Lessons', desc: 'Engaging classroom activities, games, and multimedia to make learning fun', icon: BookOpen, color: 'from-emerald-600 to-green-600' },
              { num: '2', title: 'Practice Sessions', desc: 'Regular speaking drills, pronunciation exercises, and peer conversations', icon: Mic, color: 'from-teal-600 to-cyan-600' },
              { num: '3', title: 'Real-Life Application', desc: 'Debates, presentations, competitions, and public speaking opportunities', icon: Trophy, color: 'from-amber-500 to-yellow-600' }
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.2 }}
                viewport={{ once: true }}
                className="relative"
              >
                <GlassCard className="text-center p-6 md:p-8 h-full">
                  <div className={`w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br ${step.color} text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl md:text-3xl font-bold shadow-lg`}>
                    {step.num}
                  </div>
                  <step.icon className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-4 text-emerald-600" />
                  <h3 className="text-xl md:text-2xl font-bold text-blue-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600 text-sm md:text-base">{step.desc}</p>
                </GlassCard>
                {i < 2 && (
                  <ArrowRight className="hidden md:block absolute top-1/3 -right-4 w-8 h-8 text-emerald-400" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-emerald-700 via-teal-700 to-cyan-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Skill Outcomes</h2>
            <p className="text-emerald-200">Measurable results from our program</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl lg:text-6xl font-bold text-yellow-300 mb-2">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-sm md:text-lg text-emerald-100">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">What People Say</h2>
            <p className="text-lg text-slate-600">Success stories from our community</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard className="h-full relative">
                  <Quote className="absolute top-4 right-4 w-8 h-8 text-emerald-200" />
                  <div className="text-4xl mb-4">{testimonial.avatar}</div>
                  <p className="text-slate-700 mb-4 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-bold text-blue-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.role}</p>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enrollment Form */}
      <section id="enroll" className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Enroll Your Child Today</h2>
            <p className="text-lg text-slate-600">Help them master the language of success</p>
          </motion.div>

          <GlassCard className="p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Student Name *</label>
                  <Input
                    required
                    value={formData.studentName}
                    onChange={(e) => setFormData({ ...formData, studentName: e.target.value })}
                    placeholder="Enter student name"
                    className="py-3"
                    data-testid="student-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Grade *</label>
                  <select
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    value={formData.grade}
                    onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                    data-testid="grade-select"
                  >
                    <option value="">Select Grade</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(g => <option key={g} value={g}>Class {g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Parent Name *</label>
                  <Input
                    required
                    value={formData.parentName}
                    onChange={(e) => setFormData({ ...formData, parentName: e.target.value })}
                    placeholder="Enter parent name"
                    className="py-3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Contact Number *</label>
                  <Input
                    type="tel"
                    required
                    value={formData.contact}
                    onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                    placeholder="Enter contact number"
                    className="py-3"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter email address"
                    className="py-3"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Why interested in this program?</label>
                  <textarea
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    rows="3"
                    value={formData.motivation}
                    onChange={(e) => setFormData({ ...formData, motivation: e.target.value })}
                    placeholder="Tell us about your goals..."
                  ></textarea>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold py-6 text-lg rounded-xl shadow-lg"
                data-testid="submit-enrollment-btn"
              >
                {loading ? 'Submitting...' : 'Submit Enrollment Request'}
              </Button>
            </form>
          </GlassCard>

          <div className="mt-6 text-center">
            <p className="text-slate-600 mb-2">Or contact us directly:</p>
            <a href="tel:9755402604" className="inline-flex items-center text-emerald-700 font-semibold hover:text-emerald-600">
              <Phone className="w-5 h-5 mr-2" />
              9755402604 | 9827782604
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Frequently Asked Questions</h2>
          </motion.div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                viewport={{ once: true }}
              >
                <GlassCard className="overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 text-left"
                    data-testid={`faq-${i}`}
                  >
                    <span className="font-bold text-blue-900 text-base md:text-lg pr-4">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  <motion.div
                    initial={false}
                    animate={{ height: openFaq === i ? 'auto' : 0, opacity: openFaq === i ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 text-slate-600">{faq.a}</div>
                  </motion.div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sticky CTA - Mobile */}
      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-emerald-700 to-teal-700 p-4 shadow-2xl lg:hidden z-40">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <span className="text-white font-bold text-sm">Start speaking confidently!</span>
          <a href="#enroll">
            <Button className="bg-yellow-400 hover:bg-yellow-500 text-emerald-900 font-bold px-6">
              Enroll Now
            </Button>
          </a>
        </div>
      </div>
    </Layout>
  );
}
